// src/components/credits/CreditBalanceCard.jsx
//
// Renders a CreditBalanceResponse. Two states:
//   has_plan: true  -> remaining / monthly gauge + plan name + consumed
//   has_plan: false -> "Untracked / no plan": consumed only, never 0/negative
// `showMoney` reveals cost_usd (superadmin only — null for everyone else).
import React from 'react';
import {
  formatCredits, formatMoney, consumedFraction, formatPeriodLabel,
} from '../../utils/creditsFormat.js';

const FONT = "'DM Sans', sans-serif";
const COLOR = { text: '#1d1d1f', muted: '#6c6c6d', accent: '#E8002D', okFg: '#10B981', border: 'rgba(0,0,0,0.08)' };
const cardStyle = {
  background: '#fff', borderRadius: 10, border: `1px solid ${COLOR.border}`,
  padding: '20px 22px', fontFamily: FONT, color: COLOR.text,
};

function Gauge({ fraction }) {
  const pct = Math.round((fraction || 0) * 100);
  const barColor = fraction >= 0.9 ? '#EF4444' : fraction >= 0.7 ? '#F59E0B' : COLOR.okFg;
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ height: 10, borderRadius: 6, background: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: barColor, transition: 'width 0.3s' }} />
      </div>
      <div style={{ fontSize: 11, color: COLOR.muted, marginTop: 5 }}>{pct}% dei crediti mensili utilizzato</div>
    </div>
  );
}

export default function CreditBalanceCard({ balance, showMoney = false, loading = false }) {
  if (loading) return <div style={{ ...cardStyle, color: COLOR.muted, fontSize: 13 }}>Caricamento saldo…</div>;
  if (!balance) return null;

  const { has_plan, plan_name, monthly_credits, consumed_credits, remaining_credits, cost_usd, period_start } = balance;
  const periodLabel = formatPeriodLabel(period_start);
  const money = showMoney ? formatMoney(cost_usd) : null;
  const fraction = consumedFraction(consumed_credits, monthly_credits);

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Saldo crediti</h3>
        {periodLabel && <span style={{ fontSize: 12, color: COLOR.muted }}>{periodLabel}</span>}
      </div>

      {has_plan ? (
        <>
          {plan_name && (
            <span style={{
              display: 'inline-block', marginTop: 10, padding: '3px 10px', borderRadius: 999,
              background: 'rgba(232,0,45,0.08)', color: COLOR.accent, fontSize: 12, fontWeight: 600,
            }}>{plan_name}</span>
          )}
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 30, fontWeight: 700 }}>{formatCredits(remaining_credits)}</span>
            <span style={{ fontSize: 14, color: COLOR.muted }}>/ {formatCredits(monthly_credits)} crediti rimanenti</span>
          </div>
          {fraction != null && <Gauge fraction={fraction} />}
          <div style={{ fontSize: 12, color: COLOR.muted, marginTop: 10 }}>
            Consumati nel periodo: <strong style={{ color: COLOR.text }}>{formatCredits(consumed_credits)}</strong>
          </div>
        </>
      ) : (
        <>
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 30, fontWeight: 700 }}>{formatCredits(consumed_credits)}</span>
            <span style={{ fontSize: 14, color: COLOR.muted }}>crediti consumati</span>
          </div>
          <div style={{ fontSize: 12, color: COLOR.muted, marginTop: 10 }}>
            Nessun piano attivo — utilizzo non tracciato rispetto a un limite.
          </div>
        </>
      )}

      {money && (
        <div style={{ fontSize: 12, color: COLOR.muted, marginTop: 8 }}>
          Costo stimato: <strong style={{ color: COLOR.text }}>{money}</strong>
        </div>
      )}
    </div>
  );
}
